/**
 * SignInPromptModal — actionable sign-in prompt for "Calculate Route"
 *
 * Replaces the old `alert('Please sign in...')` + straight-to-signIn()
 * flow. Gives the user three real paths instead of one OK button:
 *   - Continue with Google (one-tap -- straight to Google, no Cognito
 *     chooser page in between)
 *   - Sign up with email
 *   - I have an account (plain sign-in -- Cognito's own hosted page,
 *     which already offers Google there too for a returning user who
 *     doesn't remember which they used originally)
 *
 * Form preservation and post-sign-in auto-calculate are NOT handled here
 * -- they're the caller's job, same as before this modal existed. The
 * caller must call window.saveRouteForAfterSignIn() before showing this
 * (localStorage, read back by window.restorePendingRoute() + the
 * auto-click-Calculate block in main.js once the user returns signed in).
 */

const MODAL_STYLES = `
.signin-prompt-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.5);
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
}
.signin-prompt-panel {
    width: 100%;
    max-width: 380px;
    background: white;
    border-radius: 16px;
    padding: 1.75rem;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    box-shadow: 0 8px 32px rgba(0,0,0,0.25);
    text-align: center;
}
.signin-prompt-panel h3 {
    margin: 0 0 0.5rem;
    font-size: 1.2rem;
    color: #2c3e50;
}
.signin-prompt-panel p {
    margin: 0 0 1.5rem;
    font-size: 0.9rem;
    color: #546e7a;
    line-height: 1.5;
}
.signin-prompt-google {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;
    width: 100%;
    padding: 0.75rem;
    background: white;
    color: #3c4043;
    border: 1px solid #dadce0;
    border-radius: 8px;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    margin-bottom: 0.75rem;
}
.signin-prompt-google:hover {
    background: #f8f9fa;
}
.signin-prompt-email {
    width: 100%;
    padding: 0.75rem;
    background: #F5A623;
    color: #1a1a2e;
    border: none;
    border-radius: 8px;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    margin-bottom: 1rem;
}
.signin-prompt-email:hover {
    background: #e0961a;
}
.signin-prompt-existing {
    background: none;
    border: none;
    color: #1e88e5;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    text-decoration: underline;
}
.signin-prompt-close {
    position: absolute;
    top: 0.75rem;
    right: 1rem;
    background: none;
    border: none;
    font-size: 1.4rem;
    color: #90a4ae;
    cursor: pointer;
    line-height: 1;
}
`;

const GOOGLE_G_ICON = `<svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.21 1.13-.84 2.09-1.8 2.73v2.27h2.92c1.7-1.57 2.68-3.88 2.68-6.64z"/>
    <path fill="#34A853" d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.92-2.27c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.71H.96v2.34C2.44 15.98 5.48 18 9 18z"/>
    <path fill="#FBBC05" d="M3.97 10.7c-.18-.54-.28-1.11-.28-1.7s.1-1.16.28-1.7V4.96H.96C.35 6.18 0 7.55 0 9s.35 2.82.96 4.04l3.01-2.34z"/>
    <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l3.01 2.34C4.68 5.16 6.66 3.58 9 3.58z"/>
</svg>`;

/**
 * Show the dialog and wait for the user's choice. Each option navigates
 * away immediately (full-page redirect, same as the rest of this app's
 * auth flow), so there's nothing meaningful to resolve -- this exists so
 * the caller can save pending route state right before showing it.
 */
export function showSignInPrompt() {
    injectStyles();

    const overlay = document.createElement('div');
    overlay.className = 'signin-prompt-overlay';

    const panel = document.createElement('div');
    panel.className = 'signin-prompt-panel';
    panel.style.position = 'relative';
    panel.onclick = (e) => e.stopPropagation();

    panel.innerHTML = `
        <button class="signin-prompt-close" aria-label="Close">&times;</button>
        <h3>Sign in to calculate your route</h3>
        <p>Create a free account to get started — your route will be waiting!</p>
        <button class="signin-prompt-google">${GOOGLE_G_ICON} Continue with Google</button>
        <button class="signin-prompt-email">Sign up with email</button>
        <div>
            <button class="signin-prompt-existing">I have an account</button>
        </div>
    `;

    overlay.appendChild(panel);
    document.body.appendChild(overlay);

    const close = () => overlay.remove();

    overlay.addEventListener('click', close);
    panel.querySelector('.signin-prompt-close').addEventListener('click', close);
    panel.querySelector('.signin-prompt-google').addEventListener('click', () => {
        window.authService.signInWithGoogle();
    });
    panel.querySelector('.signin-prompt-email').addEventListener('click', () => {
        window.authService.signUp();
    });
    panel.querySelector('.signin-prompt-existing').addEventListener('click', () => {
        window.authService.signIn();
    });
}

function injectStyles() {
    if (document.getElementById('signin-prompt-styles')) return;
    const style = document.createElement('style');
    style.id = 'signin-prompt-styles';
    style.textContent = MODAL_STYLES;
    document.head.appendChild(style);
}
